import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import { currencyFormat } from 'helpers/utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Link } from 'react-router-dom';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import { useEffect, useState } from 'react';
import {
  getOrderByVendor,
  getOrdersByCustomer,
  Order
} from 'api/orders/_requests';
import Badge from 'components/base/Badge';
import { useSelector } from 'react-redux';
import { RootState } from 'redux-state/store';

const EComCustomerOrdersTable = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [ordersData, setOrdersData] = useState<Order[]>([]);

  const fetchOrdersData = async () => {
    const response = await (user?.role === 'customer'
      ? getOrdersByCustomer()
      : getOrderByVendor());
    setOrdersData(response);
  };

  const columns: ColumnDef<Order>[] = [
    {
      accessorKey: 'orderId',
      header: 'Order ID',
      cell: ({ row: { original } }) => {
        const { orderId } = original;
        return (
          <Link
            to={`/order-details/${original._id}`}
            className="fw-semibold line-clamp-1"
          >
            #{orderId}
          </Link>
        );
      },
      meta: {
        headerProps: { style: { minWidth: 250, width: '30%' } },
        cellProps: { className: 'pe-11' }
      }
    },
    {
      accessorKey: 'numberOfProducts',
      header: 'Number of Products',
      cell: ({ row: { original } }) => {
        const { products } = original;
        return <span className="fw-semibold">{products.length}</span>;
      },
      meta: {
        headerProps: { style: { width: '16%' } },
        cellProps: { className: 'white-space-nowrap' }
      }
    },
    {
      accessorKey: 'totalQuantity',
      header: 'Total Quantity',
      cell: ({ row: { original } }) => {
        const { products } = original;
        const totalQuantity = products.reduce(
          (acc, product) => acc + product.quantity,
          0
        );
        return <span className="fw-semibold">{totalQuantity}</span>;
      },
      meta: {
        headerProps: { style: { width: '16%' } },
        cellProps: { className: 'white-space-nowrap' }
      }
    },
    {
      accessorKey: 'price',
      header: () => 'Price',
      cell: ({ row: { original } }) =>
        currencyFormat(original.totalAmount, {
          currency: 'SAR',
          style: 'currency'
        }),
      meta: {
        headerProps: { style: { width: '10%' }, className: 'text-end' },
        cellProps: { className: 'text-end fw-semibold' }
      }
    },
    {
      accessorKey: 'status',
      header: 'Order Status',
      cell: ({ row: { original } }) => {
        const { status } = original;
        return (
          <Badge
            bg={status === 'pending' ? 'warning' : 'success'}
            variant="phoenix"
          >
            {status}
          </Badge>
        );
      },
      meta: {
        headerProps: { style: { width: '10%' } },
        cellProps: { className: 'text-body-tertiary fw-semibold' }
      }
    }
  ];

  useEffect(() => {
    fetchOrdersData();
  }, []);

  const table = useAdvanceTable({
    data: ordersData,
    columns,
    pageSize: 5,
    pagination: true,
    sortable: true
  });

  return (
    <div>
      <AdvanceTableProvider {...table}>
        <div className="border-y border-translucent">
          <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
          <AdvanceTableFooter pagination />
        </div>
      </AdvanceTableProvider>
    </div>
  );
};

export default EComCustomerOrdersTable;
